/**
 * How large the page is set: every type size and every measure is in rem
 * (styles/tokens.css), so setting the root's font size scales the page as
 * a whole, the way a browser's zoom would, while the map keeps its own
 * zoom. The choice is kept in this browser; until one is made it is 100%.
 */
export const SIZES = [75, 80, 90, 100, 110, 125] as const;
export type Size = (typeof SIZES)[number];
export const USUAL_SIZE: Size = 100;

const KEY = "text-size";

/** What was chosen in this browser, or the usual size. */
export function chosenSize(storage: Pick<Storage, "getItem"> | null): Size {
  try {
    const kept = Number(storage?.getItem(KEY));
    return SIZES.find((s) => s === kept) ?? USUAL_SIZE;
  } catch {
    return USUAL_SIZE;
  }
}

/** The next size up or down from `size`, staying at the ends. */
export function stepSize(size: Size, by: 1 | -1): Size {
  const at = SIZES.indexOf(size);
  return SIZES[Math.min(SIZES.length - 1, Math.max(0, at + by))]!;
}

/** The size the page is showing now. */
export function currentSize(): Size {
  return chosenSize({ getItem: () => document.documentElement.dataset["size"] ?? null });
}

/** Show a size, and keep it as this browser's choice. */
export function chooseSize(size: Size): void {
  document.documentElement.dataset["size"] = String(size);
  document.documentElement.style.fontSize = `${size}%`;
  try {
    window.localStorage.setItem(KEY, String(size));
  } catch {
    // Not kept: it holds for this visit.
  }
}
